<template>
  <h3 v-if="hasItems && hasDefaultSlot">
    <slot />
  </h3>
  <div
    v-if="hasItems"
    class="pb-report"
  >
    <ReportTree
      :tree="tree"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import useHasContent from '@nicolaudie-group/ng-ui-kit/composables/useHasContent'
import { usePresetRange } from '@/composables/useProfile'
import ReportTree from './ReportTree.vue'

const props = defineProps({
  item: { type: Object, default: () => ({}) },
  defaultState: { type: Boolean, default: true },
  childrenState: { type: Boolean, default: true },
})

const presetRange = usePresetRange()

const report = computed(() => props.item?.report || {
  errors: [],
  warnings: [],
  infos: [],
})

const flattenedReport = computed(() => [
  ...report.value.errors.map((error) => ({ ...error, level: 'error' })),
  ...report.value.warnings.map((warning) => ({ ...warning, level: 'warning' })),
  ...report.value.infos.map((info) => ({ ...info, level: 'info' })),
])

/**
 * Get the type (ssl.channel, ssl.mode, ssl.preset...) of the currently
 * selected item (if any) to use as root branch.
 */
const rootKind = computed(() => {
  if (props.item && props.item.data) {
    return props.item.data._type
  }
  return undefined
})

const hasItems = computed(() => !!flattenedReport.value.length)

function getBranchBase(kind, data = {}) {
  const { childrenState } = props
  const base = {
    name: data.name,
    link: data.link,
    order: data.order,
    state: typeof data.state === 'undefined' ? childrenState : data.state,
    uid: data._uid,
    reports: [],
  }
  switch (kind) {
    case 'mode':
    case 'ssl.mode':
      return {
        ...base,
        channels: {},
      }
    case 'channel':
    case 'ssl.channel':
      return {
        ...base,
        presetRanges: {},
      }
    case 'presetRange':
    case 'ssl.presetRange':
      return {
        ...base,
        presets: {},
      }
    case 'preset':
    case 'ssl.preset':
      return base
    case 'profile':
    case 'ssl.profile':
    default:
      return {
        ...base,
        state: base.state,
        modes: {},
        channels: {},
      }
  }
}

function getTree(reportEntries) {
  // Build the root branch based on the selected root item
  const root = getBranchBase(rootKind.value, {
    name: props.item.linkName,
    link: props.item.link,
    order: props.item.data?._order,
    state: props.defaultState,
    uid: props.item.data?._uid,
  })
  // Retrieve the UID of the currently selected presetRange
  const presetRangeUid = presetRange.value?.data._uid ?? null

  // For each report entry...
  for (let i = 0; i < reportEntries.length; i += 1) {
    const reportEntry = reportEntries[i]

    // Start from the root branch
    let workingBranch = root

    // If the report entry targets the profile branch, and the profile is the selected root branch
    // Add the report entry to the profile branch
    if (rootKind.value === 'ssl.profile' && reportEntry.target.item === 'profile') {
      workingBranch.reports.push(reportEntry)
    }

    // MODE

    // If the report entry targets a mode, and a mode is the selected root branch
    // Add the report entry to the mode branch
    if (rootKind.value === 'ssl.mode' && reportEntry.target.item === 'mode') {
      workingBranch.reports.push(reportEntry)
    }

    // If the report entry concerns a mode or it's leaves
    // And the current branch accepts modes
    if (reportEntry.mode && workingBranch.modes) {
      // Create/reuse a corresponding mode branch
      workingBranch.modes[reportEntry.mode._uid] = (
        workingBranch.modes[reportEntry.mode._uid] || getBranchBase('mode', reportEntry.mode)
      )
      // Set the mode branch as working branch
      workingBranch = workingBranch.modes[reportEntry.mode._uid]
      // If the report entry targets the mode
      // Add the report entry to the mode branch
      if (reportEntry.target.item === 'mode') {
        workingBranch.reports.push(reportEntry)
      }
    }

    // CHANNEL

    // If the report entry targets a channel, and a channel is the selected root branch
    // Add the report entry to the channel branch
    if (rootKind.value === 'ssl.channel' && reportEntry.target.item === 'channel') {
      workingBranch.reports.push(reportEntry)
    }

    // If the report entry concerns a channel or it's leaves
    // And the current branch accepts channels
    if (reportEntry.channel && workingBranch.channels) {
      // Create/reuse a corresponding channel branch
      workingBranch.channels[reportEntry.channel._uid] = (
        workingBranch.channels[reportEntry.channel._uid] || getBranchBase('channel', reportEntry.channel)
      )
      // Set the channel branch as working branch
      workingBranch = workingBranch.channels[reportEntry.channel._uid]
      // If the report entry targets the channel
      // Add the report entry to the channel branch
      if (reportEntry.target.item === 'channel') {
        workingBranch.reports.push(reportEntry)
      }
    }

    // PRESET RANGE

    // If the report entry targets a presetRange, and a presetRange is the selected root branch
    // Add the report entry to the presetRange branch
    if (rootKind.value === 'ssl.presetRange' && reportEntry.target.item === 'presetRange') {
      workingBranch.reports.push(reportEntry)
    }
    // If the report entry concerns a presetRange or it's leaves
    // And the current branch accepts presetRanges
    if (reportEntry.presetRange && workingBranch.presetRanges) {
      // Create/reuse a corresponding presetRange branch
      workingBranch.presetRanges[reportEntry.presetRange._uid] = (
        workingBranch.presetRanges[reportEntry.presetRange._uid] || getBranchBase('presetRange', reportEntry.presetRange)
      )
      // Set the presetRange branch as working branch
      workingBranch = workingBranch.presetRanges[reportEntry.presetRange._uid]
      // Expand the presetRange report if it's the report of the currently selected presetRange
      if (workingBranch.uid === presetRangeUid) {
        workingBranch.state = props.defaultState
      }
      // If the report entry targets the presetRange
      // Add the report entry to the presetRange branch
      if (reportEntry.target.item === 'presetRange') {
        workingBranch.reports.push(reportEntry)
      }
    }

    // PRESET

    // If the report entry targets a preset, and a preset is the selected root branch
    // Add the report entry to the preset branch
    if (rootKind.value === 'ssl.preset' && reportEntry.target.item === 'preset') {
      workingBranch.reports.push(reportEntry)
    }
    // If the report entry concerns a preset
    // And the current branch accepts presets
    if (reportEntry.preset && workingBranch.presets) {
      // Create/reuse a corresponding preset branch
      workingBranch.presets[reportEntry.preset._uid] = (
        workingBranch.presets[reportEntry.preset._uid] || getBranchBase('preset', reportEntry.preset)
      )
      // Set the preset branch as working branch
      workingBranch = workingBranch.presets[reportEntry.preset._uid]
      // If the report entry targets the preset
      // Add the report entry to the preset branch
      if (reportEntry.target.item === 'preset') {
        workingBranch.reports.push(reportEntry)
      }
    }
  }
  return root
}

const tree = computed(() => getTree(flattenedReport.value))

const hasDefaultSlot = useHasContent('default')
</script>

<style lang="scss">
@use '@nicolaudie-group/ng-ui-kit/defines/_fonts.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_colors.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_config.define.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_flexbox.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_generic.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_responsive.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_theme.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_animations.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_transitions.mixin.scss';

@use '/src/styles/_pb.scss';

.pb-report {
  padding-left: pb.$half-spacing;
}
</style>
